/**
 * Types for candidate processing system
 */
import type { Candidate, Configuration } from '~/types';

// Note: The former ProcessingContext type has been removed.
// Phase-specific logic should use `candidate.createdInPhase`, and
// resets should be performed explicitly via StateManager.

/**
 * Options for resetting candidate state during reprocessing
 */
export interface ResetOptions {
  /** Reset revenue map (when millage rates change) */
  resetRevenue?: boolean;

  /** Reset cash flows (when bond series or dates change) */
  resetCashFlows?: boolean;

  /** Reset results (always true for reprocessing) */
  resetResults?: boolean;

  /** Reset distribution strategy calculations */
  resetDistribution?: boolean;

  /** Values to preserve during reset */
  preserve?: PreserveOptions;
}

/**
 * Options for preserving calculated values during candidate reset
 */
export interface PreserveOptions {
  /** Keep millage shape calculations */
  millageShape?: boolean;

  /** Keep date/timeline calculations */
  baseTimeline?: boolean;

  /** Keep existing debt calculations */
  existingDebt?: boolean;

  /** Keep taxable value projections */
  taxableValue?: boolean;
}

/**
 * Result of processing a candidate
 */
export interface ProcessingResult {
  /** Whether processing succeeded */
  success: boolean;

  /** The processed candidate */
  candidate: Candidate;

  /** Error message if processing failed */
  error?: string;

  /** Performance metrics */
  metrics?: ProcessingMetrics;
}

/**
 * Performance metrics for processing operations
 */
export interface ProcessingMetrics {
  /** Time taken to process in milliseconds */
  processingTime: number;

  /** Phase where processing occurred */
  phase: string;

  /** Whether candidate was reprocessed */
  wasReprocessed: boolean;

  /** What was reset during reprocessing */
  resetOperations?: string[];
}

/**
 * Candidate modification for optimization scenarios
 */
export interface CandidateModification {
  /** Type of modification */
  type: 'millage' | 'bonds' | 'timeline' | 'drf' | 'custom';

  /** Description of the modification */
  description: string;

  /** Data for the modification */
  data: unknown;

  /** What needs to be reset for this modification */
  requiredResets: ResetOptions;
}

/**
 * Result of optimization operations
 */
export interface OptimizationResult {
  /** Whether optimization was successful */
  success: boolean;

  /** Optimized candidates */
  candidates: Candidate[];

  /** Best candidate from optimization */
  bestCandidate?: Candidate;

  /** Reason for failure if not successful */
  reason?: string;

  /** Number of iterations performed */
  iterations: number;

  /** Performance metrics */
  metrics?: ProcessingMetrics[];
}

// ===== OPTIMIZATION STRATEGY TYPES =====

/**
 * Core optimization strategy interface
 */
export interface OptimizationStrategy {
  /**
   * Optimizes the candidate's cashflow distribution
   * @param candidate - The candidate to optimize
   * @param configuration - The configuration containing bondSeries and drfInputs
   * @returns The optimized candidate
   */
  optimize(candidate: Candidate, configuration: Configuration): Candidate;
}

/**
 * Result of an individual optimization operation
 */
export interface SingleOptimizationResult {
  /** Whether optimization was applied */
  wasOptimized: boolean;

  /** Human-readable description of what was optimized */
  description?: string;

  /** List of specific adjustments made */
  adjustmentsMade?: string[];

  /** Original candidate before optimization */
  originalCandidate?: Candidate;

  /** Optimized candidate */
  optimizedCandidate: Candidate;
}

/**
 * Configuration for utilization optimization
 */
export interface UtilizationOptimizationConfig {
  /** Target DRF balance (default: starting balance) */
  targetBalance?: number;

  /** Tolerance for DRF balance difference (default: $5,000) */
  balanceTolerance?: number;

  /** Maximum iterations to try (default: 10) */
  maxIterations?: number;

  /** Enable detailed logging */
  enableLogging?: boolean;
}

/**
 * Configuration for five-year rule optimization
 */
export interface FiveYearRuleOptimizationConfig {
  /** Minimum principal required within 5 years (default: $10,000) */
  minimumPrincipal?: number;

  /** Enable detailed logging */
  enableLogging?: boolean;
}

/**
 * Available optimization types
 */
export enum OptimizationType {
  UTILIZATION = 'utilization',
  FIVE_YEAR_RULE = 'five-year-rule',
  MILLAGE_REDISTRIBUTION = 'millage-redistribution',
  COMBINED = 'combined',
  DRF_RECOVERY = 'drf-recovery',
}

/**
 * Optimization pipeline configuration
 */
export interface OptimizationPipelineConfig {
  /** Types of optimizations to run */
  optimizationTypes: OptimizationType[];

  /** Utilization optimization config */
  utilizationConfig?: UtilizationOptimizationConfig;

  /** Five-year rule optimization config */
  fiveYearRuleConfig?: FiveYearRuleOptimizationConfig;

  /** Stop on first successful optimization */
  stopOnFirstSuccess?: boolean;

  /** Enable detailed logging */
  enableLogging?: boolean;
}

/**
 * Context for optimization processing
 */
export interface OptimizationContext {
  /** Type of optimization being performed */
  type: OptimizationType;

  /** Configuration for the optimization */
  config: OptimizationPipelineConfig;

  /** Whether this is automatic (post-best-fit) or manual optimization */
  isAutomatic: boolean;

  /** Target candidate ID (for manual optimization) */
  targetCandidateId?: string;

  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Analysis result for DRF utilization
 */
export interface DRFAnalysis {
  /** Whether optimization is needed */
  needsOptimization: boolean;

  /** Amount of imbalance (ending - beginning) */
  imbalanceAmount: number;

  /** DRF beginning balance */
  beginningBalance: number;

  /** DRF ending balance */
  endingBalance: number;

  /** Total available capacity across all periods */
  totalAvailableCapacity: number;
}

/**
 * Information about a period that can be used for principal transfers
 */
export interface PeriodInfo {
  /** Payment date identifier */
  paymentDate: string;

  /** Current principal amount in this period */
  principalAmount: number;

  /** Whether this period has capacity for additional principal */
  hasCapacity?: boolean;

  /** Available capacity amount */
  availableCapacity?: number;
}

/**
 * Five-year rule violation information
 */
export interface FiveYearRuleViolation {
  /** Bond ID with violation */
  bondId: string;

  /** Bond name for display */
  bondName: string;

  /** Bond issue date (start of 5-year compliance window) */
  firstPrincipalDate: string;

  /** Five-year compliance deadline (5 years from bond issue date) */
  fiveYearDeadline: string;

  /** Periods within the 5-year window */
  periodsInWindow: Array<{
    paymentDate: string;
    principal: number;
    isInterestOnly: boolean;
  }>;

  /** Total principal within 5-year window */
  totalPrincipalInWindow: number;
}
